import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import FormModal from '../components/FormModal';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Edit3, ChevronLeft, Plus, Trash2, Sparkles, Upload } from 'lucide-react';
import ActiveToggle from '../components/ActiveToggle';
import ProductMasterBulkUploadModal from '../components/ProductMasterBulkUploadModal';
import {
  productMasterAPI,
  type Product,
  type ProductGroup,
  type ProductAttribute,
  type ProductSku,
} from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Skeleton } from '../components/Skeleton';
import { StaticOptionsEditor } from '../components/OptionSourceEditor';

/**
 * Master Data → Products.
 *
 * Two views in one tab: the product list, and a per-product detail where the real
 * work happens. Detail rather than a modal because configuring a product is a
 * three-step sequence you need to see all at once — declare what varies, multiply
 * it out, then prune what does not exist commercially.
 *
 * THE POINT OF THIS SCREEN: an admin declares 8 sizes + 4 colours + 2 Bluetooth
 * options — 14 entries — and presses Generate to get 64 SKUs. The alternative,
 * and what tasks make people do today, is typing all 64 by hand and 16 more every
 * time a colour is added. The live "would create N SKUs" count above the button
 * is there to make that multiplication legible before it happens.
 *
 * `StaticOptionsEditor` is reused for the value list rather than reimplemented:
 * it is the same add/remove/reorder control the task builder uses for dropdown
 * options, so the interaction is already familiar.
 */

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export default function ProductsPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const isSuperAdmin = useAuthStore((state) => state.user?.adminRole === 'super_admin');

  const [products, setProducts] = useState<Product[]>([]);
  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // null = list view; a product = its detail view.
  const [selected, setSelected] = useState<Product | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [groupId, setGroupId] = useState('');
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const load = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const [pRes, gRes] = await Promise.all([
        productMasterAPI.listProducts(selectedProjectId, { includeInactive: true }),
        productMasterAPI.listGroups(selectedProjectId, false),
      ]);
      setProducts(pRes.data?.data || []);
      setGroups(gRes.data?.data || []);
    } catch (err) {
      setError(apiMessage(err, 'Failed to load products.'));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setCode('');
    setGroupId('');
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditing(product);
    setName(product.name);
    setCode(product.code ?? '');
    setGroupId(product.groupId ?? '');
    setFormError(null);
    setIsModalOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    setIsSubmitting(true);
    setFormError(null);
    try {
      const body = { name, code: code || null, groupId: groupId || null };
      if (editing) {
        await productMasterAPI.updateProduct(selectedProjectId, editing.id, body);
      } else {
        await productMasterAPI.createProduct(selectedProjectId, body);
      }
      setIsModalOpen(false);
      await load();
    } catch (err) {
      setFormError(apiMessage(err, 'Failed to save the product.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete, as distinct from switching off. For a typo or a bad import — rows
  // that should never have existed. The API refuses when answers or live task
  // questions depend on the product, and its message names what is in the way.
  const removeProduct = async (product: Product) => {
    if (!selectedProjectId) return;
    if (!confirm(
      `Delete "${product.name}" permanently?\n\n`
      + `This also removes its ${product.skuCount ?? 0} version(s) and its characteristics. `
      + 'To keep the history and just take it out of use, switch it off instead.'
    )) return;
    try {
      await productMasterAPI.deleteProduct(selectedProjectId, product.id);
      await load();
    } catch (err) {
      // The API refuses when answers or live task questions depend on the row,
      // and its message names what. Show exactly that, then offer the override —
      // so the destructive path is only ever taken with the consequence in view.
      // Only offer the override to a super admin — the API refuses it for anyone
      // else, and prompting for a destructive action that will be denied is a
      // worse experience than not offering it. The server check is the real gate;
      // this only keeps the prompt honest.
      const code = (err as any)?.response?.data?.code;
      if (isSuperAdmin && (code === 'PRODUCT_HAS_ANSWERS' || code === 'PRODUCT_IN_USE')) {
        const reason = apiMessage(err, '');
        if (confirm(`${reason}\n\nDelete it anyway, and permanently remove that data too?`)) {
          try {
            await productMasterAPI.deleteProduct(selectedProjectId, product.id, true);
            await load();
            return;
          } catch (forceErr) {
            setError(apiMessage(forceErr, 'Failed to delete the product.'));
            return;
          }
        }
        return;
      }
      setError(apiMessage(err, 'Failed to delete the product.'));
    }
  };

  const toggleActive = async (product: Product) => {
    if (!selectedProjectId) return;
    try {
      await productMasterAPI.setProductActive(selectedProjectId, product.id, !product.active);
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to update the product.'));
    }
  };

  // Import sits to the LEFT of Add: for a real catalogue the spreadsheet is the
  // primary route in and adding one product by hand is the exception, but Add
  // stays the visually primary control because it is the safe, reversible one.
  const addButton = (
    <>
      <button
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        onClick={() => setIsUploadOpen(true)}
      >
        <Upload className="h-4 w-4" /> Import
      </button>
      <button
        className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
        onClick={openCreate}
      >
        Add Product
      </button>
    </>
  );

  // Detail view owns the whole tab — including the action slot, which becomes a
  // Back control so the shell's button row never shows "Add Product" while you
  // are two levels down configuring one.
  if (selected) {
    const backButton = (
      <button
        onClick={() => { setSelected(null); load(); }}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
      >
        <ChevronLeft className="h-4 w-4" /> All Products
      </button>
    );
    return (
      <>
        {actionSlot ? createPortal(backButton, actionSlot) : (
          <div className="mb-4 flex items-center gap-4">{backButton}</div>
        )}
        <ProductDetail
          projectId={selectedProjectId as number}
          product={selected}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
        </div>
      )}

      {/* The catalogue IS this page now. It used to sit behind a Catalogue /
          Stock sub-tab strip nested inside the Products tab — two levels of
          tabs to reach a list that is the obvious thing to show first. The
          running count moved up to a top-level tab of its own, where it reads
          as the separate question it is. */}
      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end gap-4">{addButton}</div>
      )}

      {error && <FormErrorBanner message={error} />}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Product</th>
                <th className="whitespace-nowrap px-6 py-4">Folder</th>
                <th className="px-6 py-4">Versions differ by</th>
                <th className="whitespace-nowrap px-6 py-4">Versions</th>
                <th className="whitespace-nowrap px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    No products yet. Use <strong>Import</strong> to bring in a
                    spreadsheet, or <strong>Add Product</strong> to create one by hand.
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className={product.active ? 'hover:bg-gray-50' : 'bg-gray-50/60 text-gray-400'}>
                    <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900">
                      {product.name}
                      {product.code && (
                        <span className="ml-2 text-xs font-normal text-gray-400">{product.code}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{product.groupPath || '—'}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {product.attributeNames
                        ? <span className="text-xs">{product.attributeNames}</span>
                        : <span className="text-xs italic text-gray-400">Nothing yet</span>}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-gray-600">{product.skuCount ?? 0}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <TableActionGroup>
                        <TableActionButton action="settings" label="Configure product versions"
                          type="button"
                          onClick={() => setSelected(product)}
                        />
                        <TableActionButton action="edit" label={`Edit ${product.name}`}
                          type="button"
                          onClick={() => openEdit(product)}
                        />
                        <ActiveToggle
                          active={product.active}
                          onToggle={() => toggleActive(product)}
                          label={product.name}
                          onLabel="Active — click to deactivate this product and its versions"
                          offLabel="Inactive — click to bring it back"
                        />
                        <TableActionButton action="delete" label={`Delete ${product.name}`}
                          type="button"
                          onClick={() => removeProduct(product)}
                          title="Delete permanently"
                        />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Edit Product' : 'Add Product'}
        size="sm"
        formId="product-form"
        isSubmitting={isSubmitting}
        submitLabel="Save"
      >
        <form id="product-form" onSubmit={submit} className="space-y-4">
          {formError && <FormErrorBanner message={formError} />}

          <div>
            <label htmlFor="product-name" className="mb-1 block text-sm font-medium text-gray-700">
              Product Name
            </label>
            <input
              id="product-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Oura Ring Gen 4"
              maxLength={150}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label htmlFor="product-group" className="mb-1 block text-sm font-medium text-gray-700">
              Group
            </label>
            <select
              id="product-group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— Ungrouped —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.path}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="product-code" className="mb-1 block text-sm font-medium text-gray-700">
              Product Code <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="product-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. OURA-G4"
              maxLength={100}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </form>
      </FormModal>

      {selectedProjectId && (
        <ProductMasterBulkUploadModal
          isOpen={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          onSuccess={load}
          projectId={selectedProjectId}
        />
      )}
    </div>
  );
}

// ─── Detail: characteristics + generated SKUs ────────────────────────────────

function ProductDetail({ projectId, product }: { projectId: number; product: Product }) {
  const isSuperAdmin = useAuthStore((state) => state.user?.adminRole === 'super_admin');
  const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
  const [skus, setSkus] = useState<ProductSku[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAttr, setEditingAttr] = useState<ProductAttribute | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [attrName, setAttrName] = useState('');
  const [attrValues, setAttrValues] = useState<string[]>(['']);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [aRes, sRes] = await Promise.all([
        productMasterAPI.listAttributes(projectId, product.id),
        productMasterAPI.listSkus(projectId, { productId: product.id, includeInactive: true }),
      ]);
      setAttributes(aRes.data?.data || []);
      setSkus(sRes.data?.data || []);
    } catch (err) {
      setError(apiMessage(err, 'Failed to load this product.'));
    } finally {
      setLoading(false);
    }
  }, [projectId, product.id]);

  useEffect(() => { load(); }, [load]);

  // The multiplication, shown before it happens. This number is the whole
  // argument for the feature: 3 characteristics can mean 64 SKUs, and nobody
  // should discover that by pressing a button.
  const projectedCount = useMemo(
    () => attributes.reduce((n, a) => n * Math.max(a.values.length, 1), attributes.length ? 1 : 0),
    [attributes],
  );

  const openCreateAttr = () => {
    setEditingAttr(null);
    setAttrName('');
    setAttrValues(['']);
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEditAttr = (attr: ProductAttribute) => {
    setEditingAttr(attr);
    setAttrName(attr.name);
    setAttrValues(attr.values.length ? attr.values : ['']);
    setFormError(null);
    setIsModalOpen(true);
  };

  const submitAttr = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);
    try {
      const values = attrValues.map((v) => v.trim()).filter(Boolean);
      const body = { name: attrName, values };
      if (editingAttr) {
        await productMasterAPI.updateAttribute(projectId, editingAttr.id, body);
      } else {
        await productMasterAPI.createAttribute(projectId, product.id, body);
      }
      setIsModalOpen(false);
      await load();
    } catch (err) {
      setFormError(apiMessage(err, 'Failed to save the characteristic.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeAttr = async (attr: ProductAttribute) => {
    // Deleting a characteristic leaves existing SKUs alone — they keep the value
    // they were generated with. Say so, because "delete" reads as destructive.
    const ok = window.confirm(
      `Remove "${attr.name}"?\n\nSKUs already generated keep their ${attr.name} value; `
      + 'it just stops being offered for new combinations.',
    );
    if (!ok) return;
    try {
      await productMasterAPI.deleteAttribute(projectId, attr.id);
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to remove the characteristic.'));
    }
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    setNotice(null);
    try {
      const res = await productMasterAPI.generateSkus(projectId, product.id);
      const { created, total } = res.data?.data ?? { created: 0, total: 0 };
      setNotice(
        created === 0
          ? `No new combinations — all ${total} already exist.`
          : `Created ${created} new SKU${created === 1 ? '' : 's'}. ${total} total.`,
      );
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to generate SKUs.'));
    } finally {
      setGenerating(false);
    }
  };

  const removeSku = async (sku: ProductSku) => {
    if (!confirm(
      `Delete this version permanently?\n\n${sku.label ?? sku.skuCode ?? ''}\n\n`
      + 'To keep its history and just stop it appearing in tasks, switch it off instead.'
    )) return;
    try {
      await productMasterAPI.deleteSku(projectId, sku.id);
      await load();
    } catch (err) {
      if (isSuperAdmin && (err as any)?.response?.data?.code === 'SKU_HAS_ANSWERS') {
        const reason = apiMessage(err, '');
        if (confirm(`${reason}\n\nDelete it anyway, and permanently remove that data too?`)) {
          try {
            await productMasterAPI.deleteSku(projectId, sku.id, true);
            await load();
            return;
          } catch (forceErr) {
            setError(apiMessage(forceErr, 'Failed to delete this version.'));
            return;
          }
        }
        return;
      }
      setError(apiMessage(err, 'Failed to delete this version.'));
    }
  };

  const toggleSku = async (sku: ProductSku) => {
    try {
      await productMasterAPI.setSkuActive(projectId, sku.id, !sku.active);
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to update the SKU.'));
    }
  };

  // Column set for the SKU table, taken from the characteristics in their display
  // order. Read off the SKU rows rather than `attributes` so a SKU generated
  // before a characteristic was removed still shows the value it carries.
  const skuColumns = useMemo(() => {
    const seen = new Set<string>();
    const cols: string[] = [];
    attributes.forEach((a) => { seen.add(a.name); cols.push(a.name); });
    skus.forEach((s) => {
      Object.keys(s.attributes || {}).forEach((k) => {
        if (!seen.has(k)) { seen.add(k); cols.push(k); }
      });
    });
    return cols;
  }, [attributes, skus]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">{product.name}</h2>
        <p className="mt-1 text-sm text-gray-500">
          {product.groupPath ? `${product.groupPath} · ` : ''}
          Say what varies about this product, then generate every real combination.
        </p>
      </div>

      {error && <FormErrorBanner message={error} />}
      {notice && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {/* Characteristics */}
      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/70 px-6 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Characteristics</h3>
            <p className="text-xs text-gray-500">
              What varies — Size, Colour, Storage. Each combination becomes one SKU.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateAttr}
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-primary-300 bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add Characteristic
          </button>
        </div>

        {loading ? (
          <div className="space-y-2 px-6 py-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        ) : attributes.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-gray-500">
            Nothing varies yet. For a ring you would add <strong>Size</strong>,{' '}
            <strong>Colour</strong> and <strong>Bluetooth</strong>; for a phone,{' '}
            <strong>Storage</strong> and <strong>Colour</strong>.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {attributes.map((attr) => (
              <div key={attr.id} className="flex items-start justify-between gap-4 px-6 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {attr.name}
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      {attr.values.length} value{attr.values.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {attr.values.map((v) => (
                      <span
                        key={v}
                        className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openEditAttr(attr)}
                    aria-label={`Edit ${attr.name}`}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-indigo-600 shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAttr(attr)}
                    aria-label={`Remove ${attr.name}`}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 shadow-sm transition-all hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50/70 px-6 py-3">
              <p className="text-xs text-gray-600">
                {attributes.map((a) => `${a.values.length} ${a.name.toLowerCase()}`).join(' × ')}
                {' = '}
                <strong className="text-gray-900">{projectedCount.toLocaleString()} combinations</strong>
              </p>
              <button
                type="button"
                onClick={generate}
                disabled={generating || attributes.length === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                {generating ? 'Generating…' : 'Generate SKUs'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SKUs */}
      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="border-b border-gray-100 bg-gray-50/70 px-6 py-3">
          <h3 className="text-sm font-semibold text-gray-900">
            SKUs <span className="font-normal text-gray-400">({skus.length})</span>
          </h3>
          <p className="text-xs text-gray-500">
            Retire any combination that is not actually sold — Size 13 may not exist in Rose Gold.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-white text-left text-xs font-semibold text-gray-500">
              <tr>
                {skuColumns.map((c) => (
                  <th key={c} className="whitespace-nowrap px-6 py-3">{c}</th>
                ))}
                <th className="whitespace-nowrap px-6 py-3">SKU Code</th>
                <th className="whitespace-nowrap px-6 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan={skuColumns.length + 2} className="px-6 py-4"><Skeleton className="h-4 w-40" /></td></tr>
              ) : skus.length === 0 ? (
                <tr>
                  <td colSpan={skuColumns.length + 2} className="px-6 py-12 text-center text-sm text-gray-500">
                    No SKUs yet. Add the characteristics above, then press Generate.
                  </td>
                </tr>
              ) : (
                skus.map((sku) => (
                  <tr key={sku.id} className={sku.active ? 'hover:bg-gray-50' : 'bg-gray-50/60 text-gray-400'}>
                    {skuColumns.map((c) => (
                      <td key={c} className="whitespace-nowrap px-6 py-3 text-gray-700">
                        {sku.attributes?.[c] ?? '—'}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-6 py-3 text-gray-500">{sku.skuCode || '—'}</td>
                    <td className="whitespace-nowrap px-6 py-3">
                      <TableActionGroup>
                        <ActiveToggle
                          active={sku.active}
                          onToggle={() => toggleSku(sku)}
                          label={sku.label ?? 'this version'}
                          onLabel="Active — click to stop it appearing in tasks"
                          offLabel="Inactive — click to bring it back"
                        />
                        <TableActionButton action="delete" label="Delete this version"
                          type="button"
                          onClick={() => removeSku(sku)}
                          title="Delete permanently"
                        />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingAttr ? 'Edit Characteristic' : 'Add Characteristic'}
        size="sm"
        formId="product-attribute-form"
        isSubmitting={isSubmitting}
        submitLabel="Save"
      >
        <form id="product-attribute-form" onSubmit={submitAttr} className="space-y-4">
          {formError && <FormErrorBanner message={formError} />}

          <div>
            <label htmlFor="attr-name" className="mb-1 block text-sm font-medium text-gray-700">
              Characteristic Name
            </label>
            <input
              id="attr-name"
              type="text"
              value={attrName}
              onChange={(e) => setAttrName(e.target.value)}
              placeholder="e.g. Size"
              maxLength={100}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">Values</span>
            <StaticOptionsEditor options={attrValues} onChange={setAttrValues} />
            <p className="mt-1.5 text-xs text-gray-500">
              The order here is the order these appear in the app.
            </p>
          </div>

          {editingAttr && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Removing a value stops it being offered for new combinations. SKUs already
              generated with it are kept — retire those individually if they are gone for good.
            </div>
          )}
        </form>
      </FormModal>
    </div>
  );
}
